import { omit } from 'lodash'
import { User } from '@utils'
import { User as UserType } from '@src/types'
import { Model } from '@models'
import { Login } from '@src/models/app.types'
import { UPSERT_USER_PROPERTY } from '@src/graphql/upsertUserProperty'
import { ANSWER_REFERRED_TO_PURCHASE } from '@src/graphql/answerReferredToPurchase'
import { SWITCH_COACH } from '@src/graphql/switchCoach'
import { PREVIEW_USER_CONVERSATION_MESSAGE } from '@src/screens/NutritionistHub/graphql/previewUserConversationMessage'
import { CurrentUserQuery } from '@src/graphql/fetchUser.generated'
import { userSelector } from '@src/selectors/app'
import { ALL_USERS_AUTOCOMPLETE } from '../graphql/allUsersAutocomplete'
import { CREATE_USER } from '../graphql/createUser'
import { CREATE_THIRD_PARTY_USER } from '../graphql/createThirdPartyUser'
import { FETCH_USER } from '../graphql/fetchUser'
import { REGISTER_NOTIFICATION_TOKEN } from '../graphql/registerNotificationToken'
import { RESET_PASSWORD } from '../graphql/resetPassword'
import { RESET_PASSWORD_REQUEST } from '../graphql/resetPasswordRequest'
import { UPDATE_USER } from '../graphql/updateUser'
import { UPDATE_EMAIL } from '../graphql/updateEmail'
import commonReducers from './commonReducers'

const updateEffect = (name: string) => {
  return Model.buildEffect({
    name,
    query: UPDATE_USER,
    dataPath: 'updateUser',
    onSuccess: (user: UserType) => {
      User.onUpdate(user)
    },
    reducers: [
      {
        name: 'app/updateUserState',
        payload: (response: any) => response,
      },
    ],
  })
}

export default class Users {
  namespace = 'users'

  state = {
    ...Model.defaultState,
  }

  effects = {
    autocomplete: Model.buildEffect({
      name: `${this.namespace}/autocomplete`,
      query: ALL_USERS_AUTOCOMPLETE,
      dataPath: 'allUsersAutocomplete',
    }),

    fetch: Model.buildEffect({
      name: `${this.namespace}/fetch`,
      query: FETCH_USER,
      dataPath: 'currentUser',
      isSuccess: (response: any) => response?.id,
      onSuccess(user: UserType) {
        User.onFetch(user)
      },
      reducers: [
        {
          name: 'app/updateUserState',
          *payload(response: CurrentUserQuery['currentUser'], { select }: any) {
            const user: CurrentUserQuery['currentUser'] | null = yield select(userSelector)

            const omittedFields = ['settings', 'sensor']

            // do not reset payment method if it hasn't synced to backend yet
            if (user?.paymentMethod && !response?.paymentMethod) {
              omittedFields.push('paymentMethod')
            }

            return omit(response, omittedFields)
          },
        },
        {
          name: 'settings/updateSettings',
          payload: (response: any) => response.settings,
        },
        {
          name: 'sensor/updateSensor',
          payload: (response: any) => response.sensor,
        },
      ],
    }),

    create: Model.buildEffect({
      name: `${this.namespace}/create`,
      query: CREATE_USER,
      dataPath: 'createUser',
      isSuccess: (response: any) => response?.success,
      //eslint-disable-next-line
      *onSuccess(login: Login, { call }: any) {
        yield call(User.onCreate, login)
      },
      reducers: [{ name: 'app/updateLoginState' }],
    }),

    createThirdParty: Model.buildEffect({
      name: `${this.namespace}/createThirdParty`,
      query: CREATE_THIRD_PARTY_USER,
      dataPath: 'createThirdPartyUser',
      isSuccess: (response: any) => response?.success,
      //eslint-disable-next-line
      *onSuccess(login: Login, { call }: any) {
        yield call(User.onCreate, login)
      },
      reducers: [{ name: 'app/updateLoginState' }],
    }),

    update: updateEffect(`${this.namespace}/update`),

    updatePhoto: updateEffect(`${this.namespace}/updatePhoto`),

    updatePassword: updateEffect(`${this.namespace}/updatePassword`),

    // Dedicated email change (staged for confirmation on the backend). Separate
    // from `update` because it hits the updateEmail mutation, not updateUser.
    updateEmail: Model.buildEffect({
      name: `${this.namespace}/updateEmail`,
      query: UPDATE_EMAIL,
      dataPath: 'updateEmail',
      onSuccess: (user: UserType) => {
        User.onUpdate(user)
      },
      reducers: [
        {
          name: 'app/updateUserState',
          payload: (response: any) => response,
        },
      ],
    }),

    registerToken: Model.buildEffect({
      name: `${this.namespace}/registerToken`,
      query: REGISTER_NOTIFICATION_TOKEN,
    }),

    resetPasswordRequest: Model.buildEffect({
      name: `${this.namespace}/resetPasswordRequest`,
      query: RESET_PASSWORD_REQUEST,
    }),

    resetPassword: Model.buildEffect({
      name: `${this.namespace}/resetPassword`,
      query: RESET_PASSWORD,
      isSuccess: (response: any) => response.resetPassword.success,
    }),

    upsertUserProperty: Model.buildEffect({
      name: `${this.namespace}/upsertUserProperty`,
      query: UPSERT_USER_PROPERTY,
      dataPath: 'upsertUserProperty',
    }),

    answerReferredToPurchase: Model.buildEffect({
      name: `${this.namespace}/answerReferredToPurchase`,
      query: ANSWER_REFERRED_TO_PURCHASE,
      dataPath: 'answerReferredToPurchase',
      reducers: [{ name: 'uiFlags/fetch' }],
    }),

    switchCoach: Model.buildEffect({
      name: `${this.namespace}/switchCoach`,
      query: SWITCH_COACH,
      dataPath: 'switchCoach',
    }),

    previewMessage: Model.buildEffect({
      name: `${this.namespace}/previewMessage`,
      query: PREVIEW_USER_CONVERSATION_MESSAGE,
      dataPath: 'previewUserConversationMessage',
    }),
  }

  reducers = {
    ...Model.defaultReducers,

    fetchList: commonReducers.fetchList,
  }
}
